/**
 * CreditsAlertBar — the site-wide "you have credits" bar for a logged-in customer whose current store
 * has credits it can spend today. Links to the Credits page. The customer can hide it for the rest of
 * the browser session (per store), so it doesn't nag on every page.
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useStoreCredits } from "../../hooks/useStoreCredits";
import { useAuth } from "../../hooks/useAuth";
import { formatCurrency } from "../../utils/formatters";

const dismissKey = (storeId) => `credits_alert_dismissed_${storeId}`;

const readDismissed = (storeId) => {
  try {
    return sessionStorage.getItem(dismissKey(storeId)) === "1";
  } catch {
    return false;
  }
};

const CreditsAlertBar = () => {
  const location = useLocation();
  const { store } = useAuth();
  const { enabled, availableTotal, storeName } = useStoreCredits();
  // Keyed by store, so switching stores shows the new store's bar even if the last one was hidden.
  const [dismissedFor, setDismissedFor] = useState({});
  const dismissed = store?.id ? dismissedFor[store.id] ?? readDismissed(store.id) : false;

  if (!enabled || availableTotal <= 0 || dismissed) return null;
  if (location.pathname === "/account/credits") return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(dismissKey(store.id), "1");
    } catch {
      // Storage can be unavailable (private mode); hiding for this page view still works.
    }
    setDismissedFor((prev) => ({ ...prev, [store.id]: true }));
  };

  return (
    <div className="bg-stash-accent text-white text-center py-2 px-3 position-relative" role="status">
      <i className="bi bi-wallet2 me-2"></i>
      <span className="me-2">
        You have <strong>{formatCurrency(availableTotal)}</strong> in credits available
        {storeName ? ` for ${storeName}` : ""}.
      </span>
      <Link to="/account/credits" className="text-white fw-semibold text-decoration-underline">
        View credits
      </Link>
      <button
        type="button"
        className="btn-close btn-close-white position-absolute top-50 end-0 translate-middle-y me-3"
        aria-label="Hide credits message"
        onClick={dismiss}
      ></button>
    </div>
  );
};

export default CreditsAlertBar;
